import { useEffect, useState } from 'react'
import { noticeKey, type ArmFailure, type ArmNotice } from '../useArming'
import type { ArmState } from '../api'
import type { Site } from '../types'
import { armsAt, clock } from '../armTime'

// minutesUntil rounds up, so a warning sent ten minutes ahead reads "10".
function minutesUntil(at: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(at) - now) / 60_000))
}

function arming(name: string, minutes: number): string {
  if (minutes === 0) return `${name} arms now`
  return `${name} arms in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
}

function failed(name: string, f: ArmFailure, reason?: string): string {
  // VMS refused it as no longer possible, so "Try again" would mislead (#248).
  if (reason) return f === 'postpone' ? `Couldn't postpone ${name}'s arm: ${reason}` : `Couldn't ${f} ${name}: ${reason}`
  if (f === 'postpone') return `Couldn't postpone ${name}'s arm. Try again.`
  return `Couldn't ${f} ${name}. Try again.`
}

/**
 * The strip under the top bar: a disarmed site about to arm itself, with the
 * choices a dispatcher has before it does, a site the schedule has just
 * disarmed, and a change a dispatcher asked for that failed.
 */
export function ArmNotices({ notices, sites, states, busy, failures, reasons = {}, onArmNow, onPostpone, onDismiss, onDismissFailure }: {
  notices: ArmNotice[]
  sites: Record<string, Site>
  /** Each site's arm state, for when a site the schedule disarmed arms again. */
  states: Record<string, ArmState>
  busy: Set<string>
  failures: Record<string, ArmFailure>
  reasons?: Record<string, string>
  onArmNow: (siteId: string) => void
  onPostpone: (siteId: string, minutes: 30 | 60) => void
  onDismiss: (notice: ArmNotice) => void
  onDismissFailure: (siteId: string) => void
}) {
  // Read when mounted, then every 15s so the minutes count down. App keys
  // this strip on its notices, so a warning that has just arrived is measured
  // from a fresh reading rather than one taken before it, a minute long.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (notices.length === 0) return
    const t = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(t)
  }, [notices.length])

  if (notices.length === 0 && Object.keys(failures).length === 0) return null
  return (
    <ul aria-label="Arming" className="shrink-0 bg-zinc-900 border-b border-zinc-800 divide-y divide-zinc-800">
      {Object.entries(failures).map(([siteId, f]) => {
        const [lead, rest] = splitReason(failed(sites[siteId]?.name || siteId, f, reasons[siteId]))
        return (
          <li key={`failed|${siteId}`} role="alert" className={`${row} bg-fault/10 border-l-fault`}>
            <Icon d={icons.failed} className="text-fault" />
            <span className="flex-1 min-w-60"><b className="font-semibold text-zinc-100">{lead}</b>{rest && <span className="text-zinc-400"> {rest}</span>}</span>
            <DismissButton onClick={() => onDismissFailure(siteId)} />
          </li>
        )
      })}
      {notices.map((n) => {
        const name = sites[n.siteId]?.name || n.siteId
        if (n.kind === 'disarmed') {
          const label = `${name} was disarmed on schedule`
          const again = states[n.siteId] && armsAt(states[n.siteId])
          return (
            <li key={noticeKey(n)} role="group" aria-label={label} className={`${row} border-l-zinc-700`}>
              <Icon d={icons.disarmed} className="text-zinc-400" />
              <span className="flex-1 min-w-60">
                <b className="font-semibold text-zinc-100">{label}</b>
                {again && <span className="text-zinc-400"> · arms again at <span className="font-mono">{again}</span></span>}
              </span>
              <DismissButton onClick={() => onDismiss(n)} />
            </li>
          )
        }
        const label = arming(name, minutesUntil(n.at, now))
        const busyHere = busy.has(n.siteId)
        return (
          <li key={noticeKey(n)} role="group" aria-label={label} className={`${row} bg-attention/[0.07] border-l-attention`}>
            <Icon d={icons.arming} className="text-attention" />
            <span className="flex-1 min-w-60">
              <b className="font-semibold text-zinc-100">{label}</b>
              <span className="text-zinc-400"> · at <span className="font-mono">{clock(n.at)}</span>, on its schedule</span>
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <button disabled={busyHere} onClick={() => onArmNow(n.siteId)} className={`h-[34px] max-sm:h-11 px-3.5 rounded-md bg-attention text-sm font-bold text-zinc-950 hover:brightness-110 ${busyStyle}`}>Arm now</button>
              <span role="group" aria-label="Postpone the arm" className="flex items-center rounded-md border border-attention/55 overflow-hidden">
                <span aria-hidden="true" className="px-2.5 text-[13px] text-zinc-400">Postpone</span>
                {([30, 60] as const).map((m) => (
                  <button key={m} disabled={busyHere} onClick={() => onPostpone(n.siteId, m)} aria-label={`Postpone ${m} min`} className={`h-8 max-sm:h-11 px-3 border-l border-attention/55 text-sm font-semibold text-zinc-100 hover:bg-attention/15 ${busyStyle}`}>{m} min</button>
                ))}
              </span>
              <DismissButton onClick={() => onDismiss(n)} />
            </span>
          </li>
        )
      })}
    </ul>
  )
}

const row = 'min-h-12 px-3 sm:px-5 py-2 flex flex-wrap items-center gap-3 text-sm border-l-4'
const busyStyle = 'disabled:opacity-50 disabled:cursor-wait'

const icons = {
  failed: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 8v4M12 16h.01',
  arming: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2',
  disarmed: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM4 4l16 16',
}

// The part before a refusal's reason is what happened; the reason, or "Try
// again", reads quieter after it.
function splitReason(message: string): [string, string] {
  const at = message.search(/: |\. /)
  return at < 0 ? [message, ''] : [message.slice(0, at + 1), message.slice(at + 2)]
}

function Icon({ d, className }: { d: string; className: string }) {
  return (
    <svg aria-hidden="true" className={`w-[18px] h-[18px] shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} aria-label="Dismiss" className="w-[34px] h-[34px] max-sm:w-11 max-sm:h-11 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
      <svg aria-hidden="true" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
    </button>
  )
}

// ArmAnnouncer says each arm notice once, for a screen reader. It is one
// region that stays mounted, unlike the strip, which App re-keys on every
// change: a region mounted with its message is often not announced, and the
// strip's minute countdown re-announced every notice it held (#287).
export function ArmAnnouncer({ notices, sites }: { notices: ArmNotice[]; sites: Record<string, Site> }) {
  const keys = notices.map(noticeKey).join(',')
  const [seen, setSeen] = useState<{ keys: string; said: Set<string>; message: string }>({ keys: '', said: new Set(), message: '' })
  if (seen.keys !== keys) {
    const fresh = notices.filter((n) => !seen.said.has(noticeKey(n)))
    const last = fresh[fresh.length - 1]
    const name = last && (sites[last.siteId]?.name || last.siteId)
    setSeen({
      keys,
      said: new Set([...seen.said, ...fresh.map(noticeKey)]),
      message: !last ? seen.message : last.kind === 'arming' ? `${name} arms at ${clock(last.at)}, on its schedule.` : `${name} was disarmed on schedule.`,
    })
  }
  return <p role="status" className="sr-only">{seen.message}</p>
}
